import { useState, useEffect, useRef } from 'react';

export const useWebSocket = (url, shouldReconnect = true) => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const shouldReconnectRef = useRef(shouldReconnect);
  
  // Update shouldReconnect ref when prop changes
  useEffect(() => {
    shouldReconnectRef.current = shouldReconnect;
  }, [shouldReconnect]);

  useEffect(() => {
    // Don't connect if URL is empty or invalid
    if (!url || url === '') {
      return;
    }
    
    // Store event handlers in outer scope
    const eventHandlers = {};
    
    // Create WebSocket connection
    const connect = () => {
      try {
        const ws = new WebSocket(url);
        
        ws.onopen = () => {
          console.log('WebSocket connected');
          setConnected(true);
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
          }
          // Trigger connect handler if registered
          if (eventHandlers['connect']) {
            eventHandlers['connect']({});
          }
        };

        ws.onmessage = (event) => {
          console.log('📨 WebSocket message received:', event.data);
          try {
            const data = JSON.parse(event.data);
            const messageType = data.type;
            
            // Trigger event handler if registered for this message type
            if (eventHandlers[messageType]) {
              eventHandlers[messageType](data);
            }
            
            // Also trigger generic 'message' handler if registered
            if (eventHandlers['message']) {
              eventHandlers['message'](data);
            }
          } catch (err) {
            console.error('Error parsing WebSocket message:', err);
            // Handle as raw message
            if (eventHandlers['message']) {
              eventHandlers['message'](event.data);
            }
          }
        };

        ws.onerror = (error) => {
          console.error('❌ WebSocket error:', error);
          console.error('Error details:', {
            type: error.type,
            target: error.target,
            url: error.target?.url,
            readyState: error.target?.readyState
          });
          setConnected(false);
          // Trigger error handler if registered
          if (eventHandlers['error']) {
            eventHandlers['error'](error);
          }
        };

        ws.onclose = (event) => {
          console.log('🔌 WebSocket disconnected', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean
          });
          setConnected(false);
          // Trigger disconnect handler if registered
          if (eventHandlers['disconnect']) {
            eventHandlers['disconnect']();
          }
          // Only reconnect if it wasn't a clean close AND reconnection is enabled
          if (event.code !== 1000 && shouldReconnectRef.current) {
            reconnectTimeoutRef.current = setTimeout(() => {
              console.log('🔄 Attempting to reconnect...');
              connect();
            }, 3000);
          } else if (!shouldReconnectRef.current) {
            console.log('🛑 Reconnection disabled, not reconnecting');
          }
        };

        wsRef.current = ws;
        
        // Create a proxy object with emit method for compatibility
        const socketProxy = {
          on: (event, callback) => {
            eventHandlers[event] = callback;
            // Do NOT assign ws.onopen/onclose/onerror here — that would replace the handlers
            // defined in connect() and break reconnection + disconnect(1000) behavior.
          },
          off: (event, callback) => {
            // Remove event handler
            if (eventHandlers[event] === callback) {
              delete eventHandlers[event];
            }
            // Note: For WebSocket native events, we can't easily remove handlers
            // but removing from eventHandlers prevents the callback from being called
          },
          emit: (event, data) => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: event, ...data }));
            }
          },
          disconnect: () => {
            shouldReconnectRef.current = false;
            if (reconnectTimeoutRef.current) {
              clearTimeout(reconnectTimeoutRef.current);
              reconnectTimeoutRef.current = null;
            }
            try {
              ws.close(1000, 'client');
            } catch (_) {
              /* ignore */
            }
          },
          onmessage: null
        };
        
        setSocket(socketProxy);
      } catch (error) {
        console.error('Error creating WebSocket:', error);
        setConnected(false);
      }
    };

    connect();

    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        try {
          wsRef.current.close(1000, 'unmount');
        } catch (_) {
          /* ignore */
        }
      }
    };
  }, [url]);

  return { socket, connected };
};

